import { SystemEvent } from '@erp/shared-types';
import { WarningIcon } from '@phosphor-icons/react';
import { Label } from 'src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import type { ConfigRendererProps } from '../types';
import { EmailRuleEventSubConfig } from './sub-configs/EmailRuleEventSubConfig';

/**
 * Human-readable labels for system events
 */
const eventLabels: Record<SystemEvent, string> = {
  [SystemEvent.EMAIL_INGESTED]: 'Email Ingested',
  [SystemEvent.EMAIL_ROUTED_EMAIL_RULE]: 'Email Routed by Email Rule',
  [SystemEvent.EMAIL_ROUTED_AP_INVOICE]: 'Email Routed to AP Invoice',
  [SystemEvent.EMAIL_ROUTED_PURCHASE_ORDER]: 'Email Routed to Purchase Order',
  [SystemEvent.EMAIL_ROUTED_QUOTE_SHEET]: 'Email Routed to Quote Sheet',
  [SystemEvent.AP_INVOICE_DRAFT_CREATED]: 'AP Invoice Draft Created',
  [SystemEvent.AP_INVOICE_PENDING_APPROVAL]: 'AP Invoice Pending Approval',
  [SystemEvent.AP_INVOICE_NEEDS_REVIEW]: 'AP Invoice Needs Review',
};

/**
 * Group events by category for better UX
 */
const eventCategories: Array<{
  label: string;
  events: SystemEvent[];
}> = [
  {
    label: 'Email Events',
    events: [
      SystemEvent.EMAIL_INGESTED,
      SystemEvent.EMAIL_ROUTED_EMAIL_RULE,
      SystemEvent.EMAIL_ROUTED_AP_INVOICE,
      SystemEvent.EMAIL_ROUTED_PURCHASE_ORDER,
      SystemEvent.EMAIL_ROUTED_QUOTE_SHEET,
    ],
  },
  {
    label: 'AP Invoice Events',
    events: [
      SystemEvent.AP_INVOICE_DRAFT_CREATED,
      SystemEvent.AP_INVOICE_PENDING_APPROVAL,
      SystemEvent.AP_INVOICE_NEEDS_REVIEW,
    ],
  },
];

/**
 * Config renderer for the event_trigger step
 * Allows selecting which system event triggers this workflow
 */
export function EventTriggerConfigRenderer({
  config,
  onConfigChange,
}: ConfigRendererProps) {
  const rawEventType = config.eventType;
  const currentEventType = typeof rawEventType === 'string' ? rawEventType : '';
  const hasExistingEvent = Boolean(currentEventType);

  const isEmailRuleEvent =
    currentEventType === SystemEvent.EMAIL_ROUTED_EMAIL_RULE;

  const handleEventTypeChange = (value: string) => {
    const nextConfig: Record<string, unknown> = {
      ...config,
      eventType: value,
    };

    if (value !== SystemEvent.EMAIL_ROUTED_EMAIL_RULE) {
      delete nextConfig.emailRuleOptionId;
    }

    onConfigChange(nextConfig);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="eventType" className="text-sm font-medium">
          Trigger Event
        </Label>
        <Select value={currentEventType} onValueChange={handleEventTypeChange}>
          <SelectTrigger id="eventType" className="w-full">
            <SelectValue placeholder="Select an event..." />
          </SelectTrigger>
          <SelectContent>
            {eventCategories.map((category) => (
              <div key={category.label}>
                <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                  {category.label}
                </div>
                {category.events.map((event) => (
                  <SelectItem key={event} value={event}>
                    {eventLabels[event]}
                  </SelectItem>
                ))}
              </div>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          The workflow will automatically start when this event occurs.
        </p>
      </div>

      {isEmailRuleEvent && (
        <EmailRuleEventSubConfig
          config={config}
          onConfigChange={onConfigChange}
        />
      )}

      {/* Warning about output changes */}
      {hasExistingEvent && (
        <div className="flex items-start gap-2 p-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
          <WarningIcon className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-700 dark:text-amber-300">
            Changing the event type will modify the output schema. Any existing
            connections from this node will be removed.
          </p>
        </div>
      )}
    </div>
  );
}
